import React, { useMemo } from 'react';
import './BarnyardBackground.css';

const Cloud = React.memo(({ x, y, delay, scale }: { 
  x: number; 
  y: number;
  delay: number;
  scale: number;
}) => (
  <div 
    className="barnyard-cloud"
    style={{
      '--cloud-x': x,
      '--cloud-y': y,
      '--cloud-scale': scale,
      animationDelay: `${delay}s`
    } as React.CSSProperties}
  />
));

const Haystack = React.memo(({ x, size }: { 
  x: number; 
  size: number;
}) => (
  <div 
    className="haystack"
    style={{
      left: `${x}%`,
      transform: `scale(${size})`
    }}
  />
));

const BarnyardBackground = React.memo(() => {
  const clouds = useMemo(() => 
    Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 10 + Math.random() * 40,
      delay: Math.random() * 10,
      scale: 0.8 + Math.random() * 0.4
    })),
  []);

  const haystacks = useMemo(() => 
    Array.from({ length: 4 }).map((_, i) => ({
      id: i,
      x: 15 + i * 20 + Math.random() * 10,
      size: 0.8 + Math.random() * 0.4
    })),
  []);

  return (
    <div className="barnyard-background">
      {/* Sky */}
      <div className="barnyard-sky" />
      
      {/* Sun */}
      <div className="barnyard-sun" />
      
      {/* Clouds */}
      <div className="clouds-layer">
        {clouds.map(cloud => (
          <Cloud
            key={cloud.id}
            x={cloud.x}
            y={cloud.y}
            delay={cloud.delay}
            scale={cloud.scale}
          />
        ))}
      </div>
      
      {/* Hills */}
      <div className="barnyard-hills">
        <div className="hill hill-far" />
        <div className="hill hill-near" />
      </div>
      
      {/* Barn */}
      <div className="barn">
        <div className="barn-roof" />
        <div className="barn-body" />
        <div className="barn-door" />
        <div className="barn-window" />
      </div>
      
      {/* Windmill */}
      <div className="windmill">
        <div className="windmill-base" />
        <div className="windmill-blades">
          <div className="blade blade-1" />
          <div className="blade blade-2" />
          <div className="blade blade-3" />
          <div className="blade blade-4" />
        </div>
      </div>
      
      {/* Fence */}
      <div className="fence">
        <div className="fence-post" />
        <div className="fence-post" />
        <div className="fence-post" />
        <div className="fence-post" />
        <div className="fence-rail" />
      </div>
      
      {/* Ground */}
      <div className="barnyard-ground" />
      
      {/* Haystacks */}
      <div className="haystacks-layer">
        {haystacks.map(haystack => (
          <Haystack
            key={haystack.id}
            x={haystack.x}
            size={haystack.size}
          />
        ))}
      </div>
      
      {/* Grass patches */}
      <div className="grass-patches" />
      
      {/* Floating particles (dust/pollen) */}
      <div className="barnyard-particles" />
    </div>
  );
});

export default BarnyardBackground;